/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { useAuth, formatApiError } from "@/lib/auth";
import { useTrackModuleView } from "@/lib/activityTracker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Zap, Scale, AlertTriangle, ClipboardCheck, Pill, Syringe,
  Scissors, Sparkles, Droplets, FileText, Save, Beef,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAMACHA_COLORS = ["#C8102E", "#E45A4E", "#F4B941", "#9CB48F", "#F0EBE0"];
const FAMACHA_LABELS = ["1 · Rouge vif", "2 · Rouge", "3 · Rose", "4 · Pâle", "5 · Blanc"];

export default function QuickEntry() {
  const { loading } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [animalId, setAnimalId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [weight, setWeight] = useState("");
  const [famacha, setFamacha] = useState("");
  const [bcs, setBcs] = useState("");
  const [vermifuge, setVermifuge] = useState("");
  const [vaccine, setVaccine] = useState("");
  const [antiparasitic, setAntiparasitic] = useState("");
  const [hoofTrim, setHoofTrim] = useState(false);
  const [shearing, setShearing] = useState(false);
  const [woolKg, setWoolKg] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useTrackModuleView("animals");

  useEffect(() => {
    if (loading) return;
    axios.get(`${API}/animals`, { withCredentials: true })
      .then((res) => setAnimals(res.data?.animals || []))
      .catch(() => setAnimals([]));
  }, [loading]);

  const reset = () => {
    setWeight(""); setFamacha(""); setBcs("");
    setVermifuge(""); setVaccine(""); setAntiparasitic("");
    setHoofTrim(false); setShearing(false); setWoolKg("");
    setNotes("");
  };

  const submit = async () => {
    if (!animalId) {
      toast.error("Sélectionnez un animal d'abord.");
      return;
    }
    const payload = {
      animal_id: animalId,
      date,
      weight_kg: weight ? parseFloat(weight) : null,
      famacha_score: famacha ? parseInt(famacha, 10) : null,
      body_score: bcs ? parseFloat(bcs) : null,
      vermifuge_product: vermifuge.trim() || null,
      vaccine_name: vaccine.trim() || null,
      antiparasitic_product: antiparasitic.trim() || null,
      hoof_trim: hoofTrim,
      shearing: shearing,
      wool_kg: woolKg ? parseFloat(woolKg) : null,
      notes: notes.trim() || null,
    };
    setSaving(true);
    try {
      const res = await axios.post(`${API}/quick-entry`, payload, { withCredentials: true });
      toast.success(`✅ ${res.data.total} action${res.data.total > 1 ? "s" : ""} enregistrée${res.data.total > 1 ? "s" : ""}`, {
        description: `Animal mis à jour. La courbe GMQ est actualisée.`,
      });
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedAnimal = animals.find((a) => a.id === animalId);

  return (
    <DashboardShell allowedRoles={["client"]}>
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
          <Zap size={14} /> Saisie rapide
        </div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
          Tout enregistrer en 1 minute
        </h1>
        <p className="mt-1 text-[#5C6B5C]">
          Sélectionnez un animal, remplissez ce que vous avez fait aujourd'hui, et appuyez sur Enregistrer.
          Les champs vides sont ignorés.
        </p>
      </header>

      <div className="space-y-5 max-w-2xl">
        {/* Animal selector + date */}
        <div className="rounded-2xl border border-[#E2E4DF] bg-white p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-1.5 flex items-center gap-1.5">
                <Beef size={12} /> Animal *
              </Label>
              <Select value={animalId} onValueChange={setAnimalId}>
                <SelectTrigger data-testid="qe-animal-select" className="h-12 bg-[#FAFAF6] border-[#E2E4DF]">
                  <SelectValue placeholder="Choisir un animal…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {animals.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-[#5C6B5C]">
                      Aucun animal — <Link to="/dashboard/client/animals" className="text-[#E05A3D] underline">en ajouter</Link>
                    </div>
                  ) : animals.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="font-semibold">{a.tag || a.name || "?"}</span>
                      {a.name && a.tag && <span className="text-[#5C6B5C] ms-2">— {a.name}</span>}
                      <span className="text-[#A8AFA1] ms-2">· {a.species}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-1.5">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 bg-[#FAFAF6] border-[#E2E4DF]" data-testid="qe-date" />
            </div>
          </div>
          {selectedAnimal && (
            <p className="mt-3 text-xs text-[#5C6B5C]">
              Poids actuel : <strong className="text-[#1C3A27]">{selectedAnimal.weight_kg ? `${selectedAnimal.weight_kg} kg` : "—"}</strong>
              {" · "}Espèce : <strong className="text-[#1C3A27] capitalize">{selectedAnimal.species}</strong>
            </p>
          )}
        </div>

        {/* Numeric measurements */}
        <Section title="Mesures" icon={Scale}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Poids (kg)" icon={Scale}>
              <Input type="number" step="0.1" min="0" placeholder="ex: 45.5" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="qe-weight" />
            </Field>
            <Field label="FAMACHA (1-5)" icon={AlertTriangle}>
              <FamachaPicker value={famacha} onChange={setFamacha} />
            </Field>
            <Field label="Score corporel (1-5)" icon={ClipboardCheck}>
              <Input type="number" step="0.5" min="1" max="5" placeholder="ex: 3" value={bcs} onChange={(e) => setBcs(e.target.value)} data-testid="qe-bcs" />
            </Field>
          </div>
        </Section>

        {/* Treatments */}
        <Section title="Traitements" icon={Syringe}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Vermifuge (produit)" icon={Pill}>
              <Input placeholder="ex: Ivomec" value={vermifuge} onChange={(e) => setVermifuge(e.target.value)} data-testid="qe-vermifuge" />
            </Field>
            <Field label="Vaccin (nom)" icon={Syringe}>
              <Input placeholder="ex: Pestivac" value={vaccine} onChange={(e) => setVaccine(e.target.value)} data-testid="qe-vaccine" />
            </Field>
            <Field label="Bain antiparasitaire (produit)" icon={Droplets}>
              <Input placeholder="ex: Butox" value={antiparasitic} onChange={(e) => setAntiparasitic(e.target.value)} data-testid="qe-antiparasitic" />
            </Field>
          </div>
        </Section>

        {/* Boolean cares */}
        <Section title="Soins du jour" icon={Sparkles}>
          <div className="grid grid-cols-2 gap-3">
            <Toggle label="Onglons taillés" icon={ClipboardCheck} value={hoofTrim} onChange={setHoofTrim} testId="qe-hoof" />
            <Toggle label="Tonte effectuée" icon={Scissors} value={shearing} onChange={setShearing} testId="qe-shearing" />
          </div>
          {shearing && (
            <div className="mt-3">
              <Field label="Quantité de laine (kg)" icon={Scissors}>
                <Input type="number" step="0.1" min="0" placeholder="ex: 2.5" value={woolKg} onChange={(e) => setWoolKg(e.target.value)} data-testid="qe-wool" />
              </Field>
            </div>
          )}
        </Section>

        {/* Free notes */}
        <Section title="Notes libres" icon={FileText}>
          <Textarea
            placeholder="Tout ce que vous voulez retenir : observation, comportement, dépense, mesure spéciale…"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="qe-notes"
            className="bg-[#FAFAF6] border-[#E2E4DF]"
          />
        </Section>

        {/* Submit */}
        <div className="sticky bottom-3 sm:static pt-2">
          <Button
            onClick={submit}
            disabled={saving || !animalId}
            data-testid="qe-submit"
            className="w-full h-14 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-bold text-base shadow-lg"
          >
            <Save size={18} className="me-2" />
            {saving ? "Enregistrement…" : "Enregistrer tout"}
          </Button>
        </div>
      </div>
    </DashboardShell>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl border border-[#E2E4DF] bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#E05A3D]" />
        <h2 className="font-display text-lg font-bold text-[#1C3A27]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </Label>
      {children}
    </div>
  );
}

function Toggle({ label, icon: Icon, value, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
        value
          ? "border-[#E05A3D] bg-[#E05A3D]/5 text-[#E05A3D]"
          : "border-[#E2E4DF] bg-white text-[#5C6B5C] hover:border-[#1C3A27]/30"
      }`}
    >
      <span className="flex items-center gap-2 font-semibold text-sm">
        <Icon size={14} /> {label}
      </span>
      <span className={`w-5 h-5 rounded-md border-2 grid place-items-center ${
        value ? "border-[#E05A3D] bg-[#E05A3D]" : "border-[#E2E4DF]"
      }`}>
        {value && <span className="text-white text-xs font-bold">✓</span>}
      </span>
    </button>
  );
}

function FamachaPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {[1, 2, 3, 4, 5].map((n, i) => {
        const active = String(value) === String(n);
        return (
          <button
            key={n}
            type="button"
            data-testid={`qe-famacha-${n}`}
            onClick={() => onChange(active ? "" : String(n))}
            className={`h-11 rounded-lg border-2 grid place-items-center text-xs font-black transition-all ${
              active ? "border-[#1C3A27] shadow-md scale-105" : "border-[#E2E4DF]"
            }`}
            style={{ backgroundColor: FAMACHA_COLORS[i], color: i >= 3 ? "#1C3A27" : "white" }}
            title={FAMACHA_LABELS[i]}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}
